/*
 * Auth configuration for the unified shell.
 *
 * PHASE 0 SOURCE: build-time Vite env (VITE_ENTRA_*), baked into the bundle per
 * env. This mirrors the plan's "re-plumb NEXT_PUBLIC_* / config → VITE_*". When
 * the edge + a public /config endpoint land, swap `loadAuthConfig()` to fetch
 * runtime config (the rest of the auth layer reads only this resolved object, so
 * that swap is one function).
 *
 * TOPOLOGY IS CONFIG-DRIVEN AND STILL-UNCONFIRMED (open_decision #1: can ONE SPA
 * app-registration expose/consume BOTH the experts AND crm API scopes with admin
 * consent, or do we need a per-resource registration?). This config supports
 * BOTH with no code rework either way:
 *
 *   - ONE registration: set VITE_ENTRA_CLIENT_ID + VITE_ENTRA_SCOPES (space- or
 *     comma-separated). Every scope mints a token from that one clientId.
 *   - PER-SCOPE map: additionally set VITE_ENTRA_SCOPE_CLIENT_MAP as JSON
 *     { "<scope>": "<clientId>", ... }. A scope listed there mints from its
 *     mapped clientId; scopes not listed fall back to the default clientId.
 *
 * msal.ts reads `clientIdForScopes(scopes)` so the redirect/silent calls target
 * the right registration. Until Josh confirms the topology, leave the map unset
 * (single-registration assumption) — flipping it on later is a pure env change.
 */

/** Environment signal — drives the E2E test-mode allow-list (prod = OFF). */
export type AuthEnvironment =
  | 'dev'
  | 'stg'
  | 'staging'
  | 'local'
  | 'test'
  | 'prd'
  | 'prod'
  | 'production'
  | 'unknown'
  | string;

export type AuthConfig = {
  authProvider: 'entra';
  /** Default SPA app-registration client id (used when a scope has no mapping). */
  clientId: string;
  /** Entra (home/CIAM) tenant id. */
  tenantId: string;
  /** Entra authority (CIAM/workforce tenant authority URL). */
  authority: string;
  /**
   * Authority HOST(s) MSAL must trust. REQUIRED for Entra External ID on a
   * custom CIAM host (e.g. andavolabs.ciamlogin.com): without it MSAL rejects the
   * non-*.microsoftonline.com authority with `untrusted_authority` and login
   * bricks before it ever reaches the IdP. Threaded into
   * msalConfig.auth.knownAuthorities.
   */
  knownAuthorities: string[];
  /** SPA redirect URI registered in Entra (the /auth/callback URL). */
  redirectUri: string;
  /** Post-logout redirect URI registered in Entra (the /signin URL). */
  postLogoutRedirectUri: string;
  /** All scopes the app requests at login (union across modules). */
  scopes: string[];
  /**
   * OPTIONAL per-scope → clientId map. Present only if the topology turns out to
   * need per-resource registrations. Absent ⇒ single-registration (all scopes
   * use `clientId`).
   */
  scopeClientMap?: Record<string, string>;
  /** Resolved environment (server- or build-provided). Gates the E2E seam. */
  environment: AuthEnvironment;
  /** Sentry DSN (optional). */
  sentryDsn: string | null;
};

/** Split a space- or comma-separated scope string into a clean array. */
function parseScopes(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse the optional scope→clientId JSON map; tolerate malformed input. */
function parseScopeClientMap(
  raw: string | undefined,
): Record<string, string> | undefined {
  if (!raw || !raw.trim()) return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
  } catch {
    // Malformed map env — ignore and fall back to single-registration. (A bad
    // map must not brick auth; the default clientId still serves every scope.)
  }
  return undefined;
}

/**
 * Resolve the auth config from build-time env.
 *
 * Documented placeholders (set per env at BUILD time; .env.example documents them):
 *   VITE_ENTRA_CLIENT_ID                — SPA app-registration (Application) client id
 *   VITE_ENTRA_TENANT_ID                — home/CIAM tenant id
 *   VITE_ENTRA_AUTHORITY                — https://<sub>.ciamlogin.com/<tenantId>
 *   VITE_ENTRA_KNOWN_AUTHORITY          — the authority HOST, e.g. andavolabs.ciamlogin.com
 *                                         (REQUIRED for External ID / custom CIAM host — without it
 *                                         MSAL throws untrusted_authority)
 *   VITE_ENTRA_REDIRECT_URI             — e.g. https://app.andavolabs.com/auth/callback
 *   VITE_ENTRA_POST_LOGOUT_REDIRECT_URI — e.g. /signin (or absolute https://app.andavolabs.com/signin)
 *   VITE_ENTRA_SCOPES                   — space/comma list (the login-time union across surfaces)
 *   VITE_ENTRA_SCOPE_CLIENT_MAP         — OPTIONAL JSON map (only if per-resource registrations are needed)
 *   VITE_ENTRA_ENVIRONMENT              — dev | stg | prd | ... (gates the E2E seam)
 *   VITE_SENTRY_DSN                     — optional
 */
export function loadAuthConfig(): AuthConfig {
  const env = import.meta.env;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // Derive the authority host for knownAuthorities if not explicitly set.
  const authority = env.VITE_ENTRA_AUTHORITY ?? '';
  const knownAuthority =
    env.VITE_ENTRA_KNOWN_AUTHORITY ||
    (() => {
      try {
        return authority ? new URL(authority).host : '';
      } catch {
        return '';
      }
    })();
  // Accept the doc's canonical VITE_ENTRA_POST_LOGOUT_REDIRECT_URI; fall back to
  // the older VITE_ENTRA_POST_LOGOUT_URI, then the current origin's /signin. A
  // root-relative value (e.g. "/signin") is resolved against the SPA origin so
  // MSAL gets an absolute, registered URI.
  const postLogoutRaw =
    env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI ??
    env.VITE_ENTRA_POST_LOGOUT_URI ??
    '/signin';
  const postLogoutRedirectUri = postLogoutRaw.startsWith('/')
    ? `${origin}${postLogoutRaw}`
    : postLogoutRaw;

  return {
    authProvider: 'entra',
    clientId: env.VITE_ENTRA_CLIENT_ID ?? '',
    tenantId: env.VITE_ENTRA_TENANT_ID ?? '',
    authority,
    knownAuthorities: knownAuthority ? [knownAuthority] : [],
    // Default the redirect URIs to the current origin so local dev "just works"
    // without env (the real registered URIs are baked per env at build time).
    redirectUri: env.VITE_ENTRA_REDIRECT_URI ?? `${origin}/auth/callback`,
    postLogoutRedirectUri,
    scopes: parseScopes(env.VITE_ENTRA_SCOPES),
    scopeClientMap: parseScopeClientMap(env.VITE_ENTRA_SCOPE_CLIENT_MAP),
    environment: (env.VITE_ENTRA_ENVIRONMENT ?? 'unknown') as AuthEnvironment,
    sentryDsn: env.VITE_SENTRY_DSN ?? null,
  };
}

/**
 * Resolve which app-registration clientId should mint a token for the given
 * scopes. The scope-parameterization seam: a chat call passes the chat scope, a
 * CRM call passes the CRM scope; each resolves to its own registration if a map
 * is configured, else the single default clientId.
 *
 * If scopes map to MORE THAN ONE clientId, that's a topology error (a single
 * acquireTokenSilent call can only target one registration) — we throw so it
 * surfaces loudly rather than minting a token with the wrong audience.
 */
export function clientIdForScopes(
  config: AuthConfig,
  scopes: string[],
): string {
  if (!config.scopeClientMap) return config.clientId;
  const mapped = new Set(
    scopes.map((s) => config.scopeClientMap?.[s] ?? config.clientId),
  );
  if (mapped.size > 1) {
    throw new Error(
      `acquireToken received scopes spanning multiple app-registrations ` +
        `(${[...mapped].join(', ')}). Request one resource's scopes per call.`,
    );
  }
  return [...mapped][0] ?? config.clientId;
}
